import { supabase } from "../supabase/SupabaseClient";
import { createRow } from "./supabaseRealtime";
import { actual } from "./quien";
import { hoyAr } from "../shared/fechaAr";

/* Licencias del personal: partes de enfermo, vacaciones y francos.
 *
 * ── QUIÉN ESCRIBE Y QUIÉN LEE, LO DECIDE LA BASE ─────────────────────────────
 *
 * La tabla `licencias` pide `ver.rrhh` para leer y `cargar.rrhh` para
 * escribir — el mismo par de llaves que la nómina, porque es el mismo tipo de
 * dato: quién trabaja y quién no. Sin la llave, Supabase devuelve cero filas
 * o rechaza el insert; la pantalla sólo decide cómo contarlo.
 *
 * También se carga por WhatsApp (`/licencia`, sólo Erick y Vilte): las dos
 * puertas escriben la misma tabla, así que lo cargado por el bot aparece acá
 * y lo cargado acá aparece en el patio del bot.
 *
 * El `tipo` es texto libre y hoy tiene DIECISIETE valores —el histórico de
 * ausentismo trajo ART, suspensiones y ausencias—. Los rótulos están en
 * `shared/licenciaTipos`, no acá: este módulo trae filas, no las nombra.
 *
 * ── ANULAR NO BORRA ──────────────────────────────────────────────────────────
 *
 * Una licencia mal cargada se anula: queda la fila, con quién la anuló y
 * cuándo. Borrarla dejaría la pregunta «¿quién la sacó?» sin respuesta — la
 * misma regla que anular un movimiento.
 */

/* `nombre` y `legajo` VAN EN EL SELECT, y su ausencia era el bug: la fila los
 * tiene siempre —cero de 3.903 sin nombre— pero la consulta no los pedía, así
 * que la pantalla caía al DNI y, en las 3.039 filas que el histórico de
 * ausentismo trajo sin DNI, escribía «DNI null». El nombre no se deduce del
 * padrón: viaja con la licencia. */
const CAMPOS =
  "id,dni,legajo,nombre,tipo,desde,hasta,nota,usuario,usuario_nombre," +
  "origen,creado_en,anulado_en,anulado_por";

/* Las licencias de UNA persona, para sus dos tarjetas de la ficha. Trae
 * también las anuladas: en un historial, una licencia que se cargó y se
 * anuló es historia, no ruido. La ficha las marca. */
export async function licenciasDe(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  if (!d) return [];
  const { data, error } = await supabase
    .from("licencias")
    .select(CAMPOS)
    .eq("dni", d)
    .order("desde", { ascending: false })
    .limit(100);
  if (error) throw error;
  return data || [];
}

/* Todas las recientes, para la pantalla de carga de RRHH. Un año alcanza:
 * la pantalla es de trabajo, no de archivo — el archivo es la ficha. */
export async function traerLicencias() {
  const hace1anio = new Date(Date.now() - 365 * 24 * 3600 * 1000)
    .toISOString()
    .slice(0, 10);
  const { data, error } = await supabase
    .from("licencias")
    .select(CAMPOS)
    .gte("desde", hace1anio)
    .order("desde", { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}

/* Pasa por `createRow` para que quede SELLADA con quién la cargó — una
 * licencia anónima es justo la fila que un día nadie puede defender. La
 * clave es `id` autogenerada, así que va sin id. */
export const crearLicencia = ({ dni, tipo, desde, hasta, nota }) =>
  createRow("licencias", null, {
    dni: String(dni).replace(/\D/g, ""),
    tipo,
    desde,
    hasta,
    nota: (nota || "").trim() || null,
  });

/* Para el deduplicado de la carga masiva de vacaciones (P-182): las vacaciones
 * ya cargadas de un conjunto de personas, como Set de «dni|desde». Re-subir la
 * misma planilla no duplica lo que ya está. Las anuladas no cuentan. */
export async function vacacionesCargadasDe(dnis) {
  const limpios = [
    ...new Set((dnis || []).map((d) => String(d).replace(/\D/g, "")).filter(Boolean)),
  ];
  if (!limpios.length) return new Set();
  const { data, error } = await supabase
    .from("licencias")
    .select("dni,desde")
    .eq("tipo", "vacaciones")
    .is("anulado_en", null)
    .in("dni", limpios);
  if (error) throw error;
  const s = new Set();
  for (const r of data || []) s.add(`${r.dni}|${r.desde}`);
  return s;
}

/* NO pasa por `updateRow` a propósito: `sellar` pisaría `usuario` con el
 * anulador y la fila perdería a quién la CARGÓ. El anulador queda en
 * `anulado_por`, que es su columna. */
export async function anularLicencia(id) {
  const quien = actual();
  const { data, error } = await supabase
    .from("licencias")
    .update({
      anulado_en: new Date().toISOString(),
      anulado_por: quien?.mail || null,
    })
    .eq("id", id)
    .is("anulado_en", null)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/* EN QUÉ ESTADO ESTÁ, que son CINCO y no dos.
 *
 * Tratarlo como un booleano —corre o no corre— es lo que hacía que una
 * licencia del 21/09 al 28/09 se dibujara «terminada» un 29/08: no corre hoy,
 * y el único otro casillero que había era el de las que ya pasaron. Lo que
 * todavía no empezó no terminó.
 *
 * Y `hasta` vacío es su propio estado, no una licencia infinita. Antes daba
 * vigente POR ACCIDENTE: `"2026-08-29" <= String(null)` es `true` porque
 * compara texto y los dígitos van antes que las letras. Con eso, 2.704 filas
 * —2.313 anteriores a 2020, la más vieja de 2007— quedaban corriendo hoy, y
 * pintaban a 110 personas del padrón como ausentes en este momento.
 *
 * El dato dice que ese vacío no es «un parte todavía abierto» sino una
 * columna que el import del histórico nunca trajo: está parejo en todos los
 * años (95 de 96 en 2016, 69 de 69 en 2023) y sólo 7 son de los últimos dos
 * meses. Así que no se afirma que corra ni se le inventa un cierre: se
 * muestra que le falta el fin, que es lo único cierto. */
export function estadoDeLicencia(l, hoy = hoyAr()) {
  if (!l) return "incompleta";
  if (l.anulado_en) return "anulada";
  const desde = l.desde ? String(l.desde).slice(0, 10) : "";
  const hasta = l.hasta ? String(l.hasta).slice(0, 10) : "";
  if (!desde) return "incompleta";
  if (desde > hoy) return "por_empezar";
  if (!hasta) return "sin_fin";
  return hasta >= hoy ? "en_curso" : "terminada";
}

/* ¿Está corriendo hoy? Puro, lo usan la ficha y la pantalla. Es UNA sola
 * respuesta del estado, para no tener dos reglas que puedan discrepar. */
export function vigente(l, hoy = hoyAr()) {
  return estadoDeLicencia(l, hoy) === "en_curso";
}

/* Cómo se dice cada estado en pantalla. La que no empezó dice CUÁNDO empieza:
 * «no está corriendo» no ayuda a nadie a planificar la semana. */
export function rotuloDeEstado(estado, l, fecha = (v) => String(v ?? "")) {
  switch (estado) {
    case "anulada":
      return { texto: "anulada", pill: "gris" };
    case "por_empezar":
      return { texto: `arranca el ${fecha(l?.desde)}`, pill: "ojo" };
    case "en_curso":
      return { texto: "en curso", pill: "ok" };
    case "sin_fin":
      return { texto: "sin fecha de fin", pill: "ojo" };
    case "terminada":
      return { texto: "terminada", pill: "gris" };
    default:
      return { texto: "sin fecha de inicio", pill: "ojo" };
  }
}

/* QUIÉN LA CARGÓ, y «—» sólo cuando de verdad no se sabe.
 *
 * Las 3.903 filas del histórico de ausentismo no tienen usuario porque las
 * trajo el bot, y para la gente de la empresa el bot se llama CEPI. Decir
 * «—» ahí es decir «no sé» de algo que sí se sabe. */
export function cargadaPor(l) {
  return l?.usuario_nombre || l?.usuario || (l?.origen ? "CEPI" : "—");
}
